import { Component, HostListener, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { merge } from 'lodash';

import { UrlParamsService } from '@app/core';
import { slideDownUpTrigger } from '@app/core/animations';
import { AppService, ErrorHandlerService, FolderService, NotyService, UserService } from '@app/services';
import { Folder, ListFormat, User } from '@app/models';
import { FoldersListingService } from '../shared/folders-listing.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-folders-list',
  templateUrl: './folders-list.component.html',
  styleUrls: ['./folders-list.component.scss'],
  animations: [slideDownUpTrigger],
  standalone: false
})
export class FoldersListComponent implements OnInit {
  currentUser: User;
  ListFormat = ListFormat;
  folders: Folder[];
  isLoading = false;
  isLastPage = false;
  pageNumber: number;
  searchQuery: any;
  mobileSearch = '';
  isSearchOpen = false;
  openTaxonomyMenu = false;
  isMobile = false;
  memberId: string = null;

  constructor(
    public urlParamsService: UrlParamsService,
    private appService: AppService,
    private folderService: FolderService,
    private foldersListingService: FoldersListingService,
    private errorHandlerService: ErrorHandlerService,
    private userService: UserService,
    private notyService: NotyService,
    private translateService: TranslateService,
    public route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.isMobile = window.innerWidth <= 992;
    this.memberId = this.route.snapshot.params.memberId || null;

    this.loadFolders();
    this.userService.getCurrentUser().subscribe({
      next: user => {
        this.currentUser = user;
      }
    });
  }

  loadFolders(options: { page?: number; search?: any } = {}) {
    this.isLoading = true;
    this.pageNumber = options.page || 1;
    this.folderService
      .folders(
        {
          ...(options.search || {}),
          memberId: this.memberId,
          limit: 60,
          page: this.pageNumber,
          orderBy: { field: this.appService.listSorting, direction: this.appService.listDirection }
        },
        this.pageNumber === 1 ? 'cache-and-network' : 'no-cache'
      )
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: page => {
          this.folders = (this.pageNumber === 1 ? [] : this.folders || []).concat(page.data);
          this.isLastPage = !page || !page.has_more_pages;
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  openFoldersModal(folder: Folder) {
    this.foldersListingService.openFoldersModal(this.folders, folder).subscribe();
  }

  deleteFolder(folder: Folder) {
    this.foldersListingService.delete(this.folders, folder).subscribe({
      complete: () => this.notyService.success(this.translateService.instant('notyService.folderDeletedSuccess')),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  openCreateFolderModal() {
    this.foldersListingService.openCreateFolderModal().subscribe({
      next: folder => this.folders.unshift(folder),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  openUpdateFolderModal(folder: Folder) {
    this.foldersListingService.openUpdateFolderModal(folder).subscribe({
      next: newFolder => merge(folder, newFolder)
    });
  }

  openShareFolderModal(folder: Folder) {
    this.foldersListingService.openShareFolderModal({ folder, organizationId: this.currentUser.organization.id }).subscribe();
  }

  clearFilters() {
    this.mobileSearch = '';
    this.searchQuery = null;
    this.isSearchOpen = false;
    this.loadFolders({ page: 1 });
  }

  toggleSearch(value: boolean) {
    this.isSearchOpen = value;
    if (this.isSearchOpen) {
      this.searchQuery = {};
    } else {
      this.clearFilters();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.isMobile = window.innerWidth <= 992;
  }
}
